// src/pages/CitizenDashboard.tsx

import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  FileText,
  TrendingUp,
  RefreshCw,
  Shield,
  BarChart3
} from 'lucide-react';
import { apiService } from '../services/api';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CitizenDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { refreshAuth, citizen: authCitizen, user: authUser } = useAuth();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const isAuthenticated = apiService.isAuthenticated();
    if (!isAuthenticated) {
      setError('Authentication required. Please login.');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    fetchMyReports();
  }, [navigate]);

  const fetchMyReports = async () => {
    try {
      setError(null);
      setLoading(true);
      refreshAuth();

      // Method 1: Use the authenticated /my-reports endpoint (preferred after email login)
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'https://connect-app-v1-0-5.onrender.com'}/api/v1/citizen-reports/my-reports`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setReports(data.data || []);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('my-reports endpoint failed, trying fallback:', err);
      }

      // Method 2: Fallback — fetch by phone number from stored citizen profile
      const citizenStr = localStorage.getItem('citizen');
      if (citizenStr) {
        const citizen = JSON.parse(citizenStr);
        const phoneNumber = citizen.phoneNumber || citizen.phone_number;
        if (phoneNumber) {
          const response = await apiService.getCitizenReports(phoneNumber);
          if (response.data.success) {
            setReports(response.data.data || []);
            setLoading(false);
            return;
          }
        }
      }

      // No reports found is OK for new users
      setReports([]);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('token');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('Failed to load reports. Please try again.');
        setReports([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewReport = () => navigate('/citizen/report/new');
  const handleViewReport = (reportId: string) => navigate(`/citizen/reports/${reportId}`);
  const handleViewAllReports = () => navigate('/citizen/reports');

  const getStatusCount = (status: string) => reports.filter(r => r.status === status).length;

  const statCards = [
    {
      title: 'Total Reports',
      value: reports.length,
      icon: <FileText className="text-[#0b2045]" size={20} />,
      color: 'bg-[#0b2045]/10',
      description: 'All reports submitted',
      link: '/citizen/reports',
    },
    {
      title: 'Pending',
      value: getStatusCount('PENDING'),
      icon: <Clock className="text-[#ca8a04]" size={20} />,
      color: 'bg-[#fefce8]',
      description: 'Awaiting review',
      link: '/citizen/reports?status=PENDING',
    },
    {
      title: 'In Progress',
      value: getStatusCount('IN_PROGRESS'),
      icon: <AlertCircle className="text-[#0b2045]" size={20} />,
      color: 'bg-[#0b2045]/10',
      description: 'Being addressed',
      link: '/citizen/reports?status=IN_PROGRESS',
    },
    {
      title: 'Resolved',
      value: getStatusCount('RESOLVED'),
      icon: <CheckCircle className="text-[#3c8d2e]" size={20} />,
      color: 'bg-[#3c8d2e]/10',
      description: 'Successfully resolved',
      link: '/citizen/reports?status=RESOLVED',
    },
    {
      title: 'Urgent Reports',
      value: reports.filter(r => r.urgencyScore >= 7).length,
      icon: <AlertCircle className="text-red-500" size={20} />,
      color: 'bg-red-50',
      description: 'High priority issues',
    },
    {
      title: 'Resolution Rate',
      value:
        reports.length > 0
          ? `${((getStatusCount('RESOLVED') / reports.length) * 100).toFixed(1)}%`
          : '0%',
      icon: <TrendingUp className="text-[#3c8d2e]" size={20} />,
      color: 'bg-[#3c8d2e]/10',
      description: 'Resolved vs Total',
    },
  ];

  const recentReports = reports.slice(0, 5);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="text-red-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] font-medium"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-[#0b2045]" size={40} />
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0b2045]">Citizen Dashboard</h1>
          <p className="text-gray-600">Track your reports and community updates</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleNewReport}
            className="px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] flex items-center gap-2"
          >
            <FileText size={18} />
            New Report
          </button>
          <Link
            to="/citizen/reports"
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-[#f4f7f6] flex items-center gap-2"
          >
            <BarChart3 size={18} />
            View All Reports
          </Link>
          <button
            onClick={fetchMyReports}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-[#f4f7f6] flex items-center gap-2"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer hover:border-[#3c8d2e]"
            onClick={() => stat.link && navigate(stat.link)}
          >
            <div className="flex items-start justify-between">
              <div className={`p-2 rounded-lg ${stat.color}`}>{stat.icon}</div>
              {stat.link && <span className="text-xs text-[#3c8d2e] font-medium">View →</span>}
            </div>
            <p className="text-sm text-gray-600 mt-3">{stat.title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
          </div>
        ))}
      </div>

      {/* Recent Reports */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-[#0b2045]">Recent Reports</h3>
            <p className="text-sm text-gray-500">Your most recent submitted reports</p>
          </div>
          <button
            onClick={handleViewAllReports}
            className="text-[#3c8d2e] hover:text-[#2d7022] font-medium text-sm flex items-center gap-1"
          >
            View All <BarChart3 size={16} />
          </button>
        </div>

        {reports.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reports yet</h3>
            <p className="text-gray-600 mb-4">You haven't submitted any reports yet.</p>
            <button
              onClick={handleNewReport}
              className="px-6 py-3 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] font-medium"
            >
              Submit Your First Report
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {recentReports.map(report => (
              <div
                key={report.id}
                className="p-6 hover:bg-[#f4f7f6] cursor-pointer transition-colors"
                onClick={() => handleViewReport(report.id)}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium text-[#0b2045]">{report.reportId}</span>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          report.status === 'PENDING'
                            ? 'bg-[#fefce8] text-[#ca8a04]'
                            : report.status === 'IN_PROGRESS'
                            ? 'bg-[#0b2045]/10 text-[#0b2045]'
                            : 'bg-[#3c8d2e]/10 text-[#3c8d2e]'
                        }`}
                      >
                        {report.status?.replace('_', ' ')}
                      </span>
                      {report.urgencyScore >= 7 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertCircle size={12} className="mr-1" />
                          Urgent
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-[#0b2045] mb-1">{report.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <MapPin size={14} className="mr-1" />
                        {report.wardName || report.constituencyName || 'Unknown location'}
                      </span>
                      <span>{new Date(report.reportedAt || report.createdAt).toLocaleDateString()}</span>
                      <span>{report.categoryDisplayName || report.category}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm text-gray-500">Urgency:</span>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-20 h-2 rounded-full ${
                          report.urgencyScore >= 7
                            ? 'bg-red-500'
                            : report.urgencyScore >= 5
                            ? 'bg-[#ca8a04]'
                            : 'bg-[#3c8d2e]'
                        }`}
                      />
                      <span className="font-medium">{report.urgencyScore}/10</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-[#f4f7f6] border border-[#0b2045]/10 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={handleNewReport}
            className="bg-white border border-[#3c8d2e]/30 rounded-lg p-4 text-left hover:bg-[#e8f1ec] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#3c8d2e]/10 rounded-lg">
                <FileText className="text-[#3c8d2e]" size={20} />
              </div>
              <div>
                <h4 className="font-medium text-[#0b2045]">Submit New Report</h4>
                <p className="text-sm text-gray-600">Report a community issue</p>
              </div>
            </div>
          </button>

          <button
            onClick={handleViewAllReports}
            className="bg-white border border-[#3c8d2e]/30 rounded-lg p-4 text-left hover:bg-[#e8f1ec] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#3c8d2e]/10 rounded-lg">
                <BarChart3 className="text-[#3c8d2e]" size={20} />
              </div>
              <div>
                <h4 className="font-medium text-[#0b2045]">View All Reports</h4>
                <p className="text-sm text-gray-600">See your report history</p>
              </div>
            </div>
          </button>

          <Link
            to="/citizen/profile"
            className="bg-white border border-[#3c8d2e]/30 rounded-lg p-4 text-left hover:bg-[#e8f1ec] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#3c8d2e]/10 rounded-lg">
                <CheckCircle className="text-[#3c8d2e]" size={20} />
              </div>
              <div>
                <h4 className="font-medium text-[#0b2045]">Update Profile</h4>
                <p className="text-sm text-gray-600">Manage your account details</p>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CitizenDashboard;